/**
 * Toast — Notificação flutuante com auto-dismiss
 * Inclui hook useToast() para uso simplificado
 */
import type { JSX } from 'preact';
import { useState, useCallback, useRef } from 'preact/hooks';
import { T, CT } from '../../styles/tokens';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose?: () => void;
  duration?: number;
}

// Ícones SVG por tipo
const icones: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

// Cores por tipo
const cores: Record<ToastType, string> = {
  success: T.success,
  error: T.danger,
  warning: T.warning,
  info: T.primary,
};

export function Toast({ message, type = 'info', onClose }: ToastProps) {
  const cor = cores[type];

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: T.space3,
      padding: `${T.space3}px ${T.space4}px`,
      background: CT.toast.bg,
      border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${cor}`,
      borderRadius: CT.toast.radius,
      boxShadow: CT.toast.shadow,
      minWidth: 280,
      maxWidth: 400,
      animation: 'slideIn 0.3s ease',
      fontSize: T.textSm,
      color: CT.toast.text,
    }}>
      <span style={{ color: cor, fontWeight: T.weightBold, fontSize: T.textLg, flexShrink: 0 }}>
        {icones[type]}
      </span>
      <span style={{ flex: 1 }}>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: T.textMuted, padding: 2, fontSize: 14, lineHeight: 1,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

// === Hook useToast ===

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const contadorRef = useRef(0);

  const show = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = ++contadorRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const remover = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Componente container para renderizar no topo da app
  function ToastContainer() {
    if (toasts.length === 0) return null;
    return (
      <div style={{
        position: 'fixed',
        top: T.space4,
        right: T.space4,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        gap: T.space2,
      }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => remover(t.id)} />
        ))}
      </div>
    );
  }

  return { show, ToastContainer };
}
